// v3.0 — hero strip, cream bg, entrance animation, urgent pulse
import { useState as useMapState, useEffect as useMapEffect, useRef as useMapRef } from 'react';
import { daysUntil, daysLabel, stepsFromAssignment } from '../lib/underground';

const DAY_LETTERS = ['S', 'M', 'Tu', 'W', 'Th', 'F', 'S'];

// Internal top band inside the SVG that holds the zone tints + zone labels
// (URGENT / DUE SOON / NEXT WEEK+) and the TODAY pill. The "LINE MAP" title
// is no longer drawn here — it now lives as an HTML station sign straddling
// the map's top border (see StudentPlanner). 22 is enough for the 8px zone
// labels with breathing room; bump to ~26 if they feel tight against the top.
const STRIP_H = 22;

function dayColor(daysFromToday) {
  if (daysFromToday < 0) return { stroke: '#e32017', text: '#e32017', opacity: 0.15 };
  if (daysFromToday <= 2) return { stroke: '#c0392b', text: '#c0392b', opacity: 0.22 };
  if (daysFromToday <= 6) return { stroke: '#c68a00', text: '#c68a00', opacity: 0.22 };
  return { stroke: '#00782a', text: '#00782a', opacity: 0.22 };
}

function isWeekend(date) {
  const d = date.getDay();
  return d === 0 || d === 6;
}

// Inject CSS once for animations
const ANIM_STYLE_ID = 'linemap-anim-styles';
function ensureAnimStyles() {
  if (document.getElementById(ANIM_STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = ANIM_STYLE_ID;
  s.textContent = `
    @keyframes lm-draw-track {
      to { stroke-dashoffset: 0; }
    }
    @keyframes lm-pop-dot {
      0%   { opacity: 0; transform: scale(0); }
      70%  { transform: scale(1.25); }
      100% { opacity: 1; transform: scale(1); }
    }
    @keyframes lm-fade-up {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes lm-urgent-pulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.45; }
    }
    .lm-track-fill {
      stroke-dashoffset: 1200;
      stroke-dasharray: 1200;
      animation: lm-draw-track 0.7s cubic-bezier(0.4,0,0.2,1) forwards;
    }
    .lm-dot {
      transform-box: fill-box;
      transform-origin: center;
      opacity: 0;
      transform: scale(0);
      animation: lm-pop-dot 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards;
    }
    .lm-label {
      opacity: 0;
      animation: lm-fade-up 0.35s ease forwards;
    }
    .lm-urgent-pulse {
      animation: lm-urgent-pulse 1.8s ease-in-out infinite;
    }
  `;
  document.head.appendChild(s);
}

export default function LineMap({ assignments, onZoneHover }) {
  const [hoveredId, setHoveredId] = useMapState(null);
  const [animKey, setAnimKey] = useMapState(0);
  const prevAssignmentIds = useMapRef('');

  useMapEffect(() => {
    ensureAnimStyles();
  }, []);

  // Re-trigger entrance animation when assignment list changes
  useMapEffect(() => {
    const ids = assignments.map(a => a.id).join(',');
    if (ids !== prevAssignmentIds.current) {
      prevAssignmentIds.current = ids;
      setAnimKey(k => k + 1);
    }
  }, [assignments]);

  if (!assignments.length) return null;

  const sorted = [...assignments].sort((a, b) => a.due_date < b.due_date ? -1 : 1);
  const now = new Date(); now.setHours(0, 0, 0, 0);

  const lineH = 90;
  const X0 = 170; // label gutter: names right-align at X0 - 40, so ~130 units of room
  const X1 = 625;
  // topPad must clear the strip (STRIP_H) plus enough room for the first
  // assignment's subject label (sits at trackY-42) and Beck diagonal (trackY-36).
  // trackY for line 0 = topPad + 28, so label sits at topPad-14.
  // We need topPad-14 >= STRIP_H + 6  →  topPad >= STRIP_H + 20
  // Use STRIP_H + 52 to give generous breathing room above the first track.
  const topPad = STRIP_H + 52;
  const total = sorted.length * lineH + topPad + 20;
  const W = 650;

  const allDates = [...new Set(sorted.map(a => a.due_date))].sort();
  const futureDates = allDates.filter(d => new Date(d + 'T00:00:00') >= now);
  const maxDateStr = futureDates.length > 0
    ? futureDates[futureDates.length - 1]
    : (() => { const d = new Date(now); d.setDate(d.getDate() + 14); return d.toISOString().slice(0, 10); })();
  const maxD = new Date(maxDateStr + 'T00:00:00'); maxD.setDate(maxD.getDate() + 2);
  const futureDays = Math.max((maxD - now) / 86400000, 7);

  const OVERDUE_WIDTH = 55;
  const todayX = X0 + OVERDUE_WIDTH;

  function daysToPixels(days) {
    return (days / futureDays) * (X1 - todayX);
  }

  function dateToX(dStr) {
    const d = new Date(dStr + 'T00:00:00'); d.setHours(0, 0, 0, 0);
    const daysFromToday = (d - now) / 86400000;
    if (daysFromToday < 0) {
      const clampedDays = Math.max(daysFromToday, -3);
      return todayX + (clampedDays / 3) * (OVERDUE_WIDTH - 20);
    }
    return Math.min(todayX + daysToPixels(daysFromToday), X1 - 10);
  }

  function daysFromNowToX(days) {
    if (days < 0) return todayX + (days / 3) * (OVERDUE_WIDTH - 20);
    return Math.min(todayX + daysToPixels(days), X1);
  }

  const urgent2X = Math.min(daysFromNowToX(2), X1);
  const soon6X = Math.min(daysFromNowToX(6), X1);

  // Zone label centre positions
  const soonMidX    = (urgent2X + soon6X) / 2;
  const distantMidX = (soon6X + X1) / 2;

  // Day grid
  const dayLines = [];
  for (let dayOffset = -3; dayOffset <= Math.ceil(futureDays) + 1; dayOffset++) {
    const x = daysFromNowToX(dayOffset);
    if (x < X0 || x > X1) continue;
    const d = new Date(now); d.setDate(d.getDate() + dayOffset);
    const col = dayColor(dayOffset);
    dayLines.push({ x, letter: DAY_LETTERS[d.getDay()], daysFromToday: dayOffset, weekend: isWeekend(d), col });
  }

  // Per-line animation delays — each line staggers after the previous
  function trackDelay(i)     { return `${0.15 + i * 0.22}s`; }
  function dotDelay(i, si)   { return `${0.15 + i * 0.22 + 0.65 + si * 0.08}s`; }
  function labelDelay(i)     { return `${0.15 + i * 0.22 + 0.65 + 0.12}s`; }

  return (
    <div style={{
      background: '#f5f0e8',
      borderRadius: 10,
      border: '6px solid #003688',
      padding: 0,
      marginBottom: 16,
      overflowX: 'auto',
      WebkitOverflowScrolling: 'touch',
    }}>
      <svg
        key={animKey}
        viewBox={`0 0 ${W} ${total}`}
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ minWidth: 520, display: 'block' }}
      >
        {/* No fixed pixel height — the viewBox ratio drives the rendered
           height so content always fills the box edge-to-edge (no bottom cream
           gap when scaled down). preserveAspectRatio keeps circles round. */}
        {/* ── CREAM BACKGROUND + GRID TEXTURE ── */}
        <rect x={0} y={0} width={W} height={total} fill="#f5f0e8"/>
        <defs>
          <pattern id="lm-grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#c8c0b0" strokeWidth="0.3" opacity="0.4"/>
          </pattern>
        </defs>
        <rect x={0} y={0} width={W} height={total} fill="url(#lm-grid)"/>

        {/* Navy strip + "LINE MAP" title removed — the title is now an HTML
           station sign straddling the map's top border (see StudentPlanner).
           STRIP_H remains as the internal top band for zone labels/tints below. */}


        {/* ── ZONE BACKGROUNDS ── */}
        {/* Zones fill the full SVG height (y=0 to bottom). Labels + day letters
           sit on top of the tint near the top; tints run edge-to-edge. */}
        {urgent2X > todayX && <rect x={todayX}  y={0} width={Math.max(0, urgent2X - todayX)}  height={total} fill="#e32017" opacity="0.13"/>}
        {soon6X > urgent2X && <rect x={urgent2X} y={0} width={Math.max(0, soon6X - urgent2X)} height={total} fill="#f3a712" opacity="0.13"/>}
        {X1 > soon6X       && <rect x={soon6X}   y={0} width={Math.max(0, X1 - soon6X)}       height={total} fill="#00782a" opacity="0.10"/>}

        {/* ── ZONE LABELS — dropped to head the day-letter row (Option 2):
             y = topPad-22 sits them ~14u above the day letters (topPad-8) so
             label + days + ticks read as one grouped header. Empty coloured
             band above is intentional "platform colour". URGENT still centres
             in the gap after the TODAY pill and only renders with room. ── */}
        {urgent2X - (todayX + 46) > 40 && <text x={(todayX + 46 + urgent2X) / 2}  y={topPad - 22} fontSize="8" fill="#c0392b" fontWeight="700" fontFamily="p22-underground, Arial" textAnchor="middle" opacity="0.6">URGENT</text>}
        {soon6X - urgent2X > 50 && <text x={soonMidX}    y={topPad - 22} fontSize="8" fill="#c68a00" fontWeight="700" fontFamily="p22-underground, Arial" textAnchor="middle" opacity="0.65">DUE SOON</text>}
        {X1 - soon6X > 50       && <text x={distantMidX} y={topPad - 22} fontSize="8" fill="#00782a" fontWeight="700" fontFamily="p22-underground, Arial" textAnchor="middle" opacity="0.65">NEXT WEEK+</text>}

        {/* ── DAY GRID LINES ── */}
        {dayLines.map((dl, i) => (
          <g key={i}>
            {dl.weekend && (
              <rect x={dl.x - 8} y={topPad - 8} width={16} height={total - topPad + 8} fill="#9e9e9e" opacity="0.04"/>
            )}
            <line
              x1={dl.x} y1={topPad - 6}
              x2={dl.x} y2={total}
              stroke={dl.col.stroke}
              strokeWidth={dl.daysFromToday === 2 || dl.daysFromToday === 6 ? 1 : 0.6}
              opacity={dl.daysFromToday === 0 ? 0 : dl.col.opacity}
            />
            {dl.daysFromToday !== 0 && (
              <text
                x={dl.x} y={topPad - 8}
                fontSize="8" fontWeight="700"
                fontFamily="p22-underground, Arial"
                textAnchor="middle"
                fill={dl.col.text}
                opacity={dl.weekend ? 0.35 : 0.6}
              >
                {dl.letter}
              </text>
            )}
          </g>
        ))}

        {/* ── TODAY LINE ── */}
        <line x1={todayX} y1={0} x2={todayX} y2={total} stroke="#e32017" strokeWidth="2" strokeDasharray="5 3" opacity="0.6"/>
        <rect x={todayX + 3} y={STRIP_H + 6} width="40" height="14" rx="3" fill="#e32017"/>
        <text x={todayX + 23} y={STRIP_H + 16} fontSize="9" fill="white" fontWeight="700" fontFamily="p22-underground, Arial" textAnchor="middle">TODAY</text>

        {/* ── ASSIGNMENTS ── */}
        {sorted.map((a, i) => {
          const steps = stepsFromAssignment(a);
          const completedCount = steps.filter(s => s.done).length;
          const progress = steps.length ? completedCount / steps.length : 0;
          const allDone = steps.length > 0 && completedCount === steps.length;
          const dueX = Math.min(Math.max(dateToX(a.due_date), X0 + 20), X1);
          const startX = X0;
          const y = topPad + i * lineH;
          const trackY = y + 28;
          const progX = startX + (dueX - startX) * progress;
          const nSteps = steps.length;
          const d = daysUntil(a.due_date);
          const isOverdue = d < 0;
          const isUrgent = !allDone && d >= 0 && d <= 2;

          const subjectLabel = (a.subject || 'Task').slice(0, 14);
          // Cut long names honestly; the full name shows on hover.
          const name = a.name || '';
          const nameLabel = name.length > 26 ? name.slice(0, 25) + '…' : name;

          const stationPositions = nSteps > 1
            ? steps.map((_, si) => startX + (si / (nSteps - 1)) * (dueX - startX))
            : [startX, dueX];

          // Track length for dasharray
          const trackLen = dueX - startX;
          const progLen  = progX - startX;

          return (
            <g key={a.id}>
              {/* Ghost track */}
              <line
                x1={startX} y1={trackY} x2={dueX} y2={trackY}
                stroke={a.color} strokeWidth="9" strokeLinecap="round"
                opacity="0.18"
                className={isUrgent ? 'lm-urgent-pulse' : ''}
              />

              {/* Animated progress fill */}
              {progress > 0 && (
                <line
                  className="lm-track-fill"
                  style={{
                    strokeDasharray: progLen,
                    strokeDashoffset: progLen,
                    animationDelay: trackDelay(i),
                  }}
                  x1={startX} y1={trackY} x2={progX} y2={trackY}
                  stroke={a.color} strokeWidth="9" strokeLinecap="round"
                />
              )}
              {/* Zero-progress track still animates (draws ghost to show it's a track) */}
              {progress === 0 && (
                <line
                  className="lm-track-fill"
                  style={{
                    strokeDasharray: trackLen,
                    strokeDashoffset: trackLen,
                    animationDelay: trackDelay(i),
                    opacity: 0,
                  }}
                  x1={startX} y1={trackY} x2={dueX} y2={trackY}
                  stroke={a.color} strokeWidth="9" strokeLinecap="round"
                />
              )}

              {/* Station dots — unified sizing */}
              {stationPositions.map((sx, si) => {
                const isEndpoint = si === 0 || si === stationPositions.length - 1;
                const filled = si < completedCount;
                return (
                  <circle
                    key={si}
                    className="lm-dot"
                    style={{ animationDelay: dotDelay(i, si) }}
                    cx={sx} cy={trackY}
                    r={isEndpoint ? 10 : 8}
                    fill={filled ? a.color : '#f5f0e8'}
                    stroke={a.color}
                    strokeWidth={filled ? '0' : '2.5'}
                  />
                );
              })}

              {/* Overdue X marker */}
              {isOverdue && (
                <g>
                  <circle cx={dueX} cy={trackY} r={13} fill="white" stroke="#7b0000" strokeWidth="2.5"/>
                  <line x1={dueX - 7} y1={trackY - 7} x2={dueX + 7} y2={trackY + 7} stroke="#7b0000" strokeWidth="2.5"/>
                  <line x1={dueX + 7} y1={trackY - 7} x2={dueX - 7} y2={trackY + 7} stroke="#7b0000" strokeWidth="2.5"/>
                </g>
              )}

              {/* Beck diagonal + labels — fade up */}
              <g className="lm-label" style={{ animationDelay: labelDelay(i) }}>
                <line
                  x1={startX - 36} y1={trackY - 36}
                  x2={startX} y2={trackY}
                  stroke={a.color} strokeWidth="1.8" opacity="0.6"
                />
                <text
                  x={startX - 40} y={trackY - 42}
                  fontSize="12" fill={a.color} fontWeight="700"
                  fontFamily="p22-underground, Arial" textAnchor="end"
                >
                  {subjectLabel}
                </text>
                <text
                  x={startX - 40} y={trackY - 28}
                  fontSize="9" fill={a.color} fontFamily="p22-underground, Arial" textAnchor="end" opacity="0.8"
                >
                  <title>{name}</title>
                  {nameLabel}
                </text>
              </g>

              {/* Due station hover */}
              <circle
                cx={dueX} cy={trackY} r={18}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => { setHoveredId(a.id); onZoneHover && onZoneHover(isOverdue ? 'overdue' : d <= 2 ? 'urgent' : d <= 6 ? 'upcoming' : 'distant'); }}
                onMouseLeave={() => { setHoveredId(null); onZoneHover && onZoneHover(null); }}
              />
              {hoveredId === a.id && (
                <g>
                  <rect x={dueX - 60} y={trackY - 38} width={120} height={22} rx={11} fill="#003688"/>
                  <text x={dueX} y={trackY - 23} fontSize="10" fill="white" fontWeight="700" fontFamily="p22-underground, Arial" textAnchor="middle">
                    {isOverdue ? 'Uh oh...' : d <= 2 ? "Let's do this!" : d <= 6 ? "Don't forget me" : 'Mind the Gap'}
                  </text>
                </g>
              )}

              {/* Due label — tightened vertical spread */}
              <text x={dueX} y={trackY - 14} fontSize="9" fill={isOverdue ? '#7b0000' : a.color} fontWeight="700" fontFamily="p22-underground, Arial" textAnchor="middle">
                {isOverdue ? 'OVERDUE' : 'Due'}
              </text>
              <text x={dueX} y={trackY + 20} fontSize="9" fill={d <= 2 && !allDone ? '#e32017' : '#6b6b6b'} fontFamily="p22-underground, Arial" textAnchor="middle">
                {daysLabel(d, allDone)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
